// =====================================================================
// Faz AU (2026-05-07) — Toplu Müşteri CSV Import
// =====================================================================
// 3-adım wizard:
//   1. CSV upload veya paste (drag-drop + textarea)
//   2. Preview (validate sayıları + ilk 10 satır + hata listesi)
//   3. Onay → Import
//
// CSV format header zorunlu:
//   email,name,company_name,phone,tax_number,address,notes
// =====================================================================
import React, { useState } from "react";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, Alert, Button, TextField, LinearProgress,
  Stepper, Step, StepLabel
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import API from "../services/api";
import toast from "react-hot-toast";

// CSV parse — quoted field destekli (basit ama RFC 4180 uyumlu)
// "abc, def" gibi virgüllü değerleri korur
function parseCsvLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      // "" → escape edilmiş tek "
      if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (c === "," && !inQuotes) {
      result.push(current);
      current = "";
    } else {
      current += c;
    }
  }
  result.push(current);
  return result.map(s => s.trim());
}

function parseCsv(text) {
  // BOM strip
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return { headers: [], rows: [] };

  const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const fields = parseCsvLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => { row[h] = fields[idx] || ""; });
    rows.push(row);
  }
  return { headers, rows };
}

const STEPS = ["1. CSV Yükle", "2. Önizle", "3. Onayla → İmport"];

export default function BulkCustomerImport() {
  const [step, setStep] = useState(0);
  const [csvText, setCsvText] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // 1. ADIM: CSV yükle
  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(e.target.result);
    reader.readAsText(file, "UTF-8");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://api.miksanplastik.com.tr/api/admin/users/bulk-import/template", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "miksan_customer_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Template indirilemedi: " + e.message);
    }
  };

  // 2. ADIM: Önizle
  const goToPreview = async () => {
    if (!csvText.trim()) { toast.error("CSV boş"); return; }
    const parsed = parseCsv(csvText);
    if (parsed.rows.length === 0) {
      toast.error("CSV satır içermiyor — başlık satırı + veri olmalı");
      return;
    }
    if (!parsed.headers.includes("email")) {
      toast.error('"email" kolonu zorunlu');
      return;
    }

    setParsedRows(parsed.rows);
    setLoading(true);
    try {
      const r = await API.post("/api/admin/users/bulk-import/preview", { rows: parsed.rows });
      setPreviewData(r.data);
      setStep(1);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // 3. ADIM: Onayla → import
  const confirmImport = async () => {
    setLoading(true);
    try {
      const r = await API.post("/api/admin/users/bulk-import/confirm", { rows: parsedRows });
      setImportResult(r.data);
      setStep(2);
      toast.success(`✓ ${r.data.inserted} müşteri eklendi`);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setCsvText("");
    setParsedRows([]);
    setPreviewData(null);
    setImportResult(null);
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h4" fontWeight={800} mb={1}>Toplu Müşteri Import</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Excel'den CSV'ye dönüştürüp toplu müşteri ekle (max 5000/seferde)
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 4 }}>
        {STEPS.map(s => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ADIM 1: CSV Yükle */}
      {step === 0 && (
        <Card sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>CSV Hazırla</Typography>
            <Button startIcon={<DownloadIcon />} onClick={downloadTemplate} variant="outlined" size="small">
              Şablon İndir
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Format:</strong> İlk satır başlık (header), sonraki satırlar veri.
            Zorunlu kolonlar: <code>email</code> + (<code>name</code> veya <code>company_name</code>).
            Opsiyonel: <code>phone, tax_number, address, notes</code>.
            Excel'de <strong>Dosya → Farklı Kaydet → CSV UTF-8</strong> seç.
          </Alert>

          {/* Drag-drop alanı */}
          <Box
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            sx={{
              border: "2px dashed #cbd5e1", borderRadius: 3, p: 4,
              textAlign: "center", bgcolor: "#f8fafc", mb: 2,
              cursor: "pointer", "&:hover": { borderColor: "#1565C0", bgcolor: "#eff6ff" }
            }}
            onClick={() => document.getElementById("csv-file-input").click()}
          >
            <CloudUploadIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
            <Typography fontWeight={600}>CSV dosyasını buraya sürükle veya tıkla</Typography>
            <Typography variant="caption" color="text.disabled">
              veya aşağıya yapıştır
            </Typography>
            <input
              id="csv-file-input" type="file" accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0])}
            />
          </Box>

          <TextField
            fullWidth multiline rows={8}
            label="Veya CSV içeriğini yapıştır"
            value={csvText}
            onChange={e => setCsvText(e.target.value)}
            placeholder={"email,name,company_name,phone\nornek@firma.com,Ahmet,ABC Ltd,05551234567"}
            sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12 } }}
          />

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setCsvText("")}>Temizle</Button>
            <Button variant="contained" onClick={goToPreview} disabled={!csvText.trim() || loading}>
              Önizle →
            </Button>
          </Box>
        </Card>
      )}

      {/* ADIM 2: Preview */}
      {step === 1 && previewData && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="caption" color="text.secondary">TOPLAM</Typography>
                <Typography variant="h4" fontWeight={800}>{previewData.summary.total}</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#f0fdf4" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>GEÇERLİ</Typography>
                <Typography variant="h4" fontWeight={800} color="#10b981">{previewData.summary.valid}</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fffbeb" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>DUPLICATE</Typography>
                <Typography variant="h4" fontWeight={800} color="#f59e0b">{previewData.summary.duplicates}</Typography>
                <Typography variant="caption" color="text.disabled">DB'de zaten var</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3, bgcolor: "#fef2f2" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>HATA</Typography>
                <Typography variant="h4" fontWeight={800} color="#ef4444">{previewData.summary.errors}</Typography>
                <Typography variant="caption" color="text.disabled">format problem</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* İlk 10 preview */}
          <Card sx={{ borderRadius: 3, mb: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
              <Typography variant="h6" fontWeight={700}>İlk 10 Satır Önizleme</Typography>
            </Box>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell width={40}>#</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>İsim / Firma</TableCell>
                  <TableCell>Telefon</TableCell>
                  <TableCell>VKN</TableCell>
                  <TableCell align="center">Durum</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.preview.map((r, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{r.email}</TableCell>
                    <TableCell>
                      {r.company_name && <Typography fontSize={13} fontWeight={600}>{r.company_name}</Typography>}
                      {r.name && r.name !== r.company_name && <Typography variant="caption" color="text.disabled">{r.name}</Typography>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.phone || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{r.tax_number || "—"}</TableCell>
                    <TableCell align="center">
                      {r.is_valid && <Chip label="✓ Eklenecek" size="small" sx={{ bgcolor: "#10b981", color: "white", fontSize: 10 }} />}
                      {r.is_duplicate && <Chip label="Duplicate" size="small" sx={{ bgcolor: "#f59e0b", color: "white", fontSize: 10 }} />}
                      {r.errors.length > 0 && <Chip label={`Hata: ${r.errors.join(", ")}`} size="small" sx={{ bgcolor: "#ef4444", color: "white", fontSize: 10 }} />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Hatalar varsa */}
          {previewData.errors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>İlk {previewData.errors.length} hatalı satır:</strong>
              <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 12 }}>
                {previewData.errors.slice(0, 10).map((e, i) => (
                  <li key={i}><code>{e.email || "(boş)"}</code> — {e.errors.join(", ")}</li>
                ))}
              </Box>
            </Alert>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button onClick={reset} startIcon={<RefreshIcon />}>← Yeniden Başla</Button>
            <Button
              variant="contained" color="success" startIcon={<CheckIcon />}
              onClick={confirmImport} disabled={loading || previewData.summary.valid === 0}
            >
              {previewData.summary.valid} Müşteriyi Import Et
            </Button>
          </Box>
        </>
      )}

      {/* ADIM 3: Sonuç */}
      {step === 2 && importResult && (
        <Card sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
          <CheckIcon sx={{ fontSize: 64, color: "#10b981", mb: 2 }} />
          <Typography variant="h5" fontWeight={800} mb={1}>İmport Tamamlandı</Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            <strong>{importResult.inserted}</strong> müşteri başarıyla eklendi.
            {importResult.skipped > 0 && <span> {importResult.skipped} satır atlandı.</span>}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}>
            <Card sx={{ p: 2, bgcolor: "#f0fdf4" }}>
              <Typography variant="caption">Eklendi</Typography>
              <Typography variant="h4" fontWeight={800} color="#10b981">{importResult.inserted}</Typography>
            </Card>
            <Card sx={{ p: 2, bgcolor: "#fffbeb" }}>
              <Typography variant="caption">Atlandı</Typography>
              <Typography variant="h4" fontWeight={800} color="#f59e0b">{importResult.skipped}</Typography>
            </Card>
          </Box>

          {importResult.errors.length > 0 && (
            <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
              <Typography fontWeight={600} mb={1}>Atlanan satırlar (ilk 20):</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 11, maxHeight: 200, overflow: "auto" }}>
                {importResult.errors.map((e, i) => (
                  <li key={i}><code>{e.email}</code> — {e.reason}</li>
                ))}
              </Box>
            </Alert>
          )}

          <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 2 }}>
            Source tag: <code>{importResult.source_tag}</code> (analytics filter için)
          </Typography>

          <Button variant="contained" onClick={reset}>Yeni Import Başlat</Button>
        </Card>
      )}
    </Box>
  );
}
